import { loadLocalImage } from './image-loader.ts';
// OnCatch #210 — Widget annotation toolbar (v3 design, codex-cleared).
//
// runAnnotationFlow opens an annotation overlay over a captured screenshot
// with 4 tools (arrow / box / freehand pen / text), 4 colors, undo + clear,
// Save + Skip. Returns a discriminated AnnotationOutcome the caller switches
// on to decide whether to upload the result.
//
// Architecture choices (see /tmp/oncatch-210-annotation-design-v3.md):
//   - Mounted as a fixed-position overlay inside the existing widget shadow
//     root (so it inherits the widget's z-index island and font-family).
//   - Drawing uses Canvas 2D primitives (no library — bundle budget).
//   - Pen tool rAF-throttles mousemove to keep redraws cheap on 4K input.
//   - Settle exactly once via a guard flag; cleanup runs in cleanup().
//   - Image decode wrapped in Promise.race with 5 s timeout (codex round 2).
//   - Mobile (pointer:coarse + hover:none) silently skips the modal.
//   - Pointer coords are mapped from CSS px to natural px via
//     getBoundingClientRect (codex round 2 confirmed pattern is correct).
//   - Focus trap reads getRootNode().activeElement (closed-shadow-correct;
//     document.activeElement returns the host, not the focused inner btn).

import { adoptStyles, styleRootFor } from './csp.ts';
import type { WidgetMessages } from './i18n/index.ts';

// ============================================================================
// Public types
// ============================================================================

export type AnnotationOutcome =
  | { outcome: 'save'; blob: Blob }
  | { outcome: 'skip'; blob: Blob }
  | { outcome: 'discard' }
  | { outcome: 'error' };

// ============================================================================
// Internal types
// ============================================================================

type Color = 'red' | 'yellow' | 'green' | 'black';
type Tool = 'arrow' | 'box' | 'pen' | 'text';
type Point = { x: number; y: number };

type Shape =
  | { type: 'arrow'; from: Point; to: Point; color: Color }
  | { type: 'box'; from: Point; to: Point; color: Color }
  | { type: 'pen'; points: Point[]; color: Color }
  | { type: 'text'; at: Point; text: string; color: Color };

const COLOR_HEX: Record<Color, string> = {
  red: '#dc2626',
  yellow: '#facc15',
  green: '#22c55e',
  black: '#171717',
};

const TEXT_MAX_CHARS = 200;
const TEXT_FONT_PX = 18;
const ARROW_HEAD_PX = 12;
const STROKE_WIDTH = 3;
const DECODE_TIMEOUT_MS = 5000;

// ============================================================================
// Mobile detection (codex round 2: wrap in try/catch; matchMedia may throw)
// ============================================================================

export function isMobileTouchOnly(): boolean {
  try {
    return window.matchMedia?.('(pointer: coarse) and (hover: none)')?.matches === true;
  } catch {
    return false;
  }
}

// ============================================================================
// Focus trap (closed-shadow-DOM correct)
// ============================================================================

function getActiveElement(modal: HTMLElement): Element | null {
  const root = modal.getRootNode();
  return root instanceof ShadowRoot ? root.activeElement : document.activeElement;
}

function trapFocus(modal: HTMLElement): () => void {
  const handler = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    const focusables = Array.from(
      modal.querySelectorAll<HTMLElement>(
        'button:not([disabled]):not([hidden]), input:not([disabled]):not([hidden]), [tabindex="0"]',
      ),
    );
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    const active = getActiveElement(modal);
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };
  modal.addEventListener('keydown', handler);
  return () => modal.removeEventListener('keydown', handler);
}

// ============================================================================
// Entry point
// ============================================================================

export async function runAnnotationFlow(
  screenshotBlob: Blob,
  mountTarget: HTMLElement,
  messages: WidgetMessages,
  subscribePanelClose: (cb: () => void) => () => void,
  previousFocus?: HTMLElement | null,
): Promise<AnnotationOutcome> {
  // Mobile + touch-only devices skip annotation entirely. Same end result
  // as Skip: raw screenshot returned, no UI shown.
  if (isMobileTouchOnly()) {
    return { outcome: 'skip', blob: screenshotBlob };
  }

  // Image decode (gated with 5 s timeout per codex round 2). Failure routes
  // to skip with the raw blob — user still gets their screenshot in.
  let img: HTMLImageElement;
  try {
    img = await loadLocalImage(screenshotBlob, DECODE_TIMEOUT_MS);
  } catch {
    return { outcome: 'skip', blob: screenshotBlob };
  }

  return new Promise<AnnotationOutcome>((resolve) => {
    // ──────────────────────────────────────────────────────────────────────
    // State
    // ──────────────────────────────────────────────────────────────────────
    const shapes: Shape[] = [];
    let pendingShape: Shape | null = null;
    let currentTool: Tool = 'arrow';
    let currentColor: Color = 'red';
    let settled = false;
    let textInput: HTMLInputElement | null = null;
    let rafId: number | null = null;

    // ──────────────────────────────────────────────────────────────────────
    // DOM
    // ──────────────────────────────────────────────────────────────────────
    const overlay = document.createElement('div');
    overlay.className = 'annotate-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', messages.annotateTitle);

    // #584: styles are ADOPTED after mount (below), not injected as a <style>
    // element here. A host CSP with `style-src-elem 'self' 'nonce-…'` blocks an
    // un-nonced <style>, which discarded every rule in buildOverlayStyles() —
    // the overlay lost `position: fixed; inset: 0` and rendered as in-flow
    // content inside the 360px panel, and the canvas lost `max-width: 100%` and
    // rendered at full natural pixel size. Reported in the wild on a Next.js
    // host. Adoption must happen after mountTarget.appendChild(overlay) so
    // getRootNode() resolves to the shadow root the overlay actually lives in.
    let releaseStyles: (() => void) | null = null;

    const canvas = document.createElement('canvas');
    canvas.className = 'annotate-canvas';
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      resolve({ outcome: 'skip', blob: screenshotBlob });
      return;
    }
    ctx.drawImage(img, 0, 0);

    // Toolbar
    const toolbar = document.createElement('div');
    toolbar.className = 'annotate-toolbar';
    toolbar.setAttribute('role', 'toolbar');
    toolbar.setAttribute('aria-label', messages.annotateTitle);

    const toolButtons = new Map<Tool, HTMLButtonElement>();
    const colorButtons = new Map<Color, HTMLButtonElement>();

    const toolLabels: Record<Tool, string> = {
      arrow: messages.toolArrow,
      box: messages.toolBox,
      pen: messages.toolPen,
      text: messages.toolText,
    };
    const colorLabels: Record<Color, string> = {
      red: messages.colorRed,
      yellow: messages.colorYellow,
      green: messages.colorGreen,
      black: messages.colorBlack,
    };

    for (const tool of ['arrow', 'box', 'pen', 'text'] as Tool[]) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'annotate-tool';
      btn.dataset.tool = tool;
      btn.setAttribute('aria-label', toolLabels[tool]);
      btn.textContent = toolLabels[tool];
      btn.addEventListener('click', () => {
        commitPending();
        currentTool = tool;
        refreshToolbarState();
      });
      toolbar.appendChild(btn);
      toolButtons.set(tool, btn);
    }

    const colorRow = document.createElement('div');
    colorRow.className = 'annotate-colors';
    for (const color of ['red', 'yellow', 'green', 'black'] as Color[]) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'annotate-color';
      btn.dataset.color = color;
      btn.style.background = COLOR_HEX[color];
      btn.setAttribute('aria-label', colorLabels[color]);
      btn.addEventListener('click', () => {
        commitPending();
        currentColor = color;
        refreshToolbarState();
      });
      colorRow.appendChild(btn);
      colorButtons.set(color, btn);
    }
    toolbar.appendChild(colorRow);

    const undoBtn = document.createElement('button');
    undoBtn.type = 'button';
    undoBtn.className = 'annotate-action';
    undoBtn.setAttribute('aria-label', messages.buttonUndo);
    undoBtn.textContent = messages.buttonUndo;
    undoBtn.addEventListener('click', () => {
      commitPending();
      shapes.pop();
      redraw();
      refreshToolbarState();
    });
    toolbar.appendChild(undoBtn);

    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'annotate-action';
    clearBtn.setAttribute('aria-label', messages.buttonClear);
    clearBtn.textContent = messages.buttonClear;
    clearBtn.addEventListener('click', () => {
      pendingShape = null;
      shapes.length = 0;
      removeTextInput();
      redraw();
      refreshToolbarState();
    });
    toolbar.appendChild(clearBtn);

    // Canvas viewport wrapper (lets us scroll if the screenshot is huge)
    const canvasWrap = document.createElement('div');
    canvasWrap.className = 'annotate-canvas-wrap';
    canvasWrap.appendChild(canvas);

    // Footer with Save + Skip
    const footer = document.createElement('div');
    footer.className = 'annotate-footer';

    const skipBtn = document.createElement('button');
    skipBtn.type = 'button';
    skipBtn.className = 'annotate-skip';
    skipBtn.textContent = messages.buttonSkip;
    skipBtn.addEventListener('click', () => {
      settle({ outcome: 'skip', blob: screenshotBlob });
    });

    const saveBtn = document.createElement('button');
    saveBtn.type = 'button';
    saveBtn.className = 'annotate-save';
    saveBtn.textContent = messages.buttonSave;
    saveBtn.addEventListener('click', () => {
      // Guard against double-click queuing two simultaneous full-canvas
      // PNG encodes (memory spike on 4K input). Disable visually + via
      // the disabled attribute the moment the click handler enters.
      if (saveBtn.disabled) return;
      commitPending();
      if (shapes.length === 0) {
        // Empty stack + Save → return original raw blob (no needless re-encode)
        settle({ outcome: 'save', blob: screenshotBlob });
        return;
      }
      saveBtn.disabled = true;
      try {
        canvas.toBlob((b) => {
          if (b) {
            settle({ outcome: 'save', blob: b });
          } else {
            settle({ outcome: 'skip', blob: screenshotBlob });
          }
        }, 'image/png');
      } catch (err) {
        // Synchronous toBlob throw (CORS-tainted canvas, OOM, etc.) →
        // settle to skip with the raw blob. Don't leave the flow pending.
        // biome-ignore lint/suspicious/noConsole: surface composite failures
        console.warn('[OnCatch] annotation composite failed:', err);
        settle({ outcome: 'skip', blob: screenshotBlob });
      }
    });

    footer.appendChild(skipBtn);
    footer.appendChild(saveBtn);

    overlay.appendChild(toolbar);
    overlay.appendChild(canvasWrap);
    overlay.appendChild(footer);
    mountTarget.appendChild(overlay);

    // #584: adopt the overlay CSS onto whichever root the overlay landed in.
    // Appends to adoptedStyleSheets (never replaces), so the widget's own
    // stylesheet is untouched; released in cleanup() so repeated open/close
    // does not accumulate a sheet per open.
    releaseStyles = adoptStyles(styleRootFor(overlay), buildOverlayStyles());

    refreshToolbarState();

    // ──────────────────────────────────────────────────────────────────────
    // Drawing handlers
    // ──────────────────────────────────────────────────────────────────────
    function pointerToCanvas(evt: MouseEvent): Point {
      const rect = canvas.getBoundingClientRect();
      const sx = canvas.width / rect.width;
      const sy = canvas.height / rect.height;
      return { x: (evt.clientX - rect.left) * sx, y: (evt.clientY - rect.top) * sy };
    }

    function onMouseDown(evt: MouseEvent) {
      if (textInput) return; // text input is open; clicks belong to it
      const p = pointerToCanvas(evt);
      if (currentTool === 'arrow') {
        pendingShape = { type: 'arrow', from: p, to: p, color: currentColor };
      } else if (currentTool === 'box') {
        pendingShape = { type: 'box', from: p, to: p, color: currentColor };
      } else if (currentTool === 'pen') {
        pendingShape = { type: 'pen', points: [p], color: currentColor };
      } else if (currentTool === 'text') {
        spawnTextInput(p, evt);
      }
      redraw();
    }

    function onMouseMove(evt: MouseEvent) {
      if (!pendingShape) return;
      const p = pointerToCanvas(evt);
      if (pendingShape.type === 'arrow' || pendingShape.type === 'box') {
        pendingShape.to = p;
        scheduleRedraw();
      } else if (pendingShape.type === 'pen') {
        pendingShape.points.push(p);
        scheduleRedraw();
      }
    }

    function onMouseUp() {
      commitPending();
    }

    function scheduleRedraw() {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        redraw();
      });
    }

    function commitPending() {
      if (!pendingShape) return;
      // Drop a degenerate shape (a click with no drag), except for text
      const s = pendingShape;
      if (s.type === 'pen' && s.points.length < 2) {
        pendingShape = null;
      } else if (
        (s.type === 'arrow' || s.type === 'box') &&
        s.from.x === s.to.x &&
        s.from.y === s.to.y
      ) {
        pendingShape = null;
      } else {
        shapes.push(s);
        pendingShape = null;
      }
      redraw();
      refreshToolbarState();
    }

    function spawnTextInput(at: Point, evt: MouseEvent) {
      removeTextInput();
      const input = document.createElement('input');
      input.type = 'text';
      input.maxLength = TEXT_MAX_CHARS;
      input.className = 'annotate-text-input';
      input.placeholder = messages.textInputPlaceholder;
      // #348 — position the input at the click point in viewport coords.
      // The overlay is `position: fixed; inset: 0` so it covers the full
      // viewport, and the input is `position: absolute` inside it — the
      // input's containing block IS the viewport. The previous calc
      // subtracted `mountTarget.getBoundingClientRect()` (the widget panel,
      // which sits in the bottom-right of the page) and produced a wildly
      // wrong offset. The input rendered far off the click point, the user
      // couldn't see or focus it, and the blur handler immediately
      // committed an empty value and removed it. From the user's POV:
      // clicked, nothing happened.
      input.style.left = `${evt.clientX}px`;
      input.style.top = `${evt.clientY}px`;
      input.style.color = COLOR_HEX[currentColor];
      // Idempotency guard: blur fires when ANY removal path runs (Esc,
      // Clear, programmatic remove during cleanup). Without this guard
      // an Esc that calls removeTextInput would also trigger blur which
      // would commit whatever was typed — defeating the cancel intent.
      let finalized = false;
      const commit = () => {
        if (finalized) return;
        finalized = true;
        const val = input.value.trim();
        if (val.length > 0) {
          shapes.push({ type: 'text', at, text: val, color: currentColor });
        }
        removeTextInput();
        redraw();
        refreshToolbarState();
      };
      const cancel = () => {
        if (finalized) return;
        finalized = true;
        removeTextInput();
      };
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
        e.stopPropagation();
      });
      input.addEventListener('blur', commit);
      overlay.appendChild(input);
      textInput = input;
      input.focus();
    }

    function removeTextInput() {
      if (textInput) {
        // Mark as finalized BEFORE detaching so the blur event (which
        // fires on .remove()) sees the guard and no-ops.
        const t = textInput;
        textInput = null;
        t.remove();
      }
    }

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('mouseleave', () => {
      if (pendingShape) commitPending();
    });

    // ──────────────────────────────────────────────────────────────────────
    // Render
    // ──────────────────────────────────────────────────────────────────────
    function redraw() {
      try {
        if (!ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        for (const s of shapes) drawShape(s);
        if (pendingShape) drawShape(pendingShape);
      } catch (err) {
        // Local catch (NOT global window.onerror per codex round 3 finding)
        // — annotation render failures shouldn't terminate the flow due to
        // unrelated host-page errors.
        // biome-ignore lint/suspicious/noConsole: surface render failures
        console.warn('[OnCatch] annotation redraw failed:', err);
      }
    }

    function drawShape(s: Shape) {
      if (!ctx) return;
      ctx.strokeStyle = COLOR_HEX[s.color];
      ctx.fillStyle = COLOR_HEX[s.color];
      ctx.lineWidth = STROKE_WIDTH;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      if (s.type === 'arrow') {
        drawArrow(s.from, s.to);
      } else if (s.type === 'box') {
        ctx.strokeRect(
          Math.min(s.from.x, s.to.x),
          Math.min(s.from.y, s.to.y),
          Math.abs(s.to.x - s.from.x),
          Math.abs(s.to.y - s.from.y),
        );
      } else if (s.type === 'pen') {
        const pts = s.points;
        const first = pts[0];
        if (pts.length < 2 || !first) return;
        ctx.beginPath();
        ctx.moveTo(first.x, first.y);
        for (let i = 1; i < pts.length; i++) {
          const p = pts[i];
          if (p) ctx.lineTo(p.x, p.y);
        }
        ctx.stroke();
      } else if (s.type === 'text') {
        ctx.font = `600 ${TEXT_FONT_PX}px system-ui, sans-serif`;
        ctx.textBaseline = 'top';
        // ctx.fillText treats input as data, NOT markup — no XSS surface.
        ctx.fillText(s.text, s.at.x, s.at.y);
      }
    }

    function drawArrow(from: Point, to: Point) {
      if (!ctx) return;
      ctx.beginPath();
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(to.x, to.y);
      ctx.stroke();
      // Arrow head
      const angle = Math.atan2(to.y - from.y, to.x - from.x);
      const h = ARROW_HEAD_PX;
      ctx.beginPath();
      ctx.moveTo(to.x, to.y);
      ctx.lineTo(
        to.x - h * Math.cos(angle - Math.PI / 6),
        to.y - h * Math.sin(angle - Math.PI / 6),
      );
      ctx.lineTo(
        to.x - h * Math.cos(angle + Math.PI / 6),
        to.y - h * Math.sin(angle + Math.PI / 6),
      );
      ctx.closePath();
      ctx.fill();
    }

    function refreshToolbarState() {
      for (const [tool, btn] of toolButtons) {
        const isActive = tool === currentTool;
        btn.classList.toggle('annotate-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      }
      for (const [color, btn] of colorButtons) {
        const isActive = color === currentColor;
        btn.classList.toggle('annotate-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      }
      const empty = shapes.length === 0 && !pendingShape;
      undoBtn.disabled = empty;
      clearBtn.disabled = empty;
    }

    // ──────────────────────────────────────────────────────────────────────
    // Keyboard (Esc + Cmd/Ctrl+Z)
    // ──────────────────────────────────────────────────────────────────────
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !textInput) {
        e.preventDefault();
        settle({ outcome: 'skip', blob: screenshotBlob });
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !textInput) {
        e.preventDefault();
        commitPending();
        shapes.pop();
        redraw();
        refreshToolbarState();
      }
    };
    overlay.addEventListener('keydown', onKey);

    // ──────────────────────────────────────────────────────────────────────
    // Focus management + trap
    // ──────────────────────────────────────────────────────────────────────
    const releaseFocusTrap = trapFocus(overlay);
    queueMicrotask(() => saveBtn.focus());

    // ──────────────────────────────────────────────────────────────────────
    // Panel-close subscription (widget close mid-flow → discard outcome)
    // ──────────────────────────────────────────────────────────────────────
    const unsubscribeClose = subscribePanelClose(() => {
      settle({ outcome: 'discard' });
    });

    // ──────────────────────────────────────────────────────────────────────
    // Cleanup + settle (exactly once)
    // ──────────────────────────────────────────────────────────────────────
    function cleanup() {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      releaseFocusTrap();
      unsubscribeClose();
      // #584: detach the adopted sheet before the overlay goes. Adopted sheets
      // live on the ROOT, not on the removed element, so skipping this would
      // leak one sheet per annotation session.
      releaseStyles?.();
      releaseStyles = null;
      overlay.remove();
      if (previousFocus && typeof previousFocus.focus === 'function') {
        try {
          previousFocus.focus();
        } catch {
          // Element may have been removed during the flow; ignore.
        }
      }
    }

    function settle(result: AnnotationOutcome) {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    }
  });
}

// ============================================================================
// Inline overlay styles
// ============================================================================

function buildOverlayStyles(): string {
  return `
  .annotate-overlay {
    position: fixed;
    inset: 0;
    z-index: 2147483647;
    background: rgba(15, 12, 8, 0.85);
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
    box-sizing: border-box;
    color: #fafafa;
  }
  .annotate-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    padding: 8px 12px;
    background: #ffffff;
    color: #171717;
    border-radius: 8px;
    margin-bottom: 12px;
  }
  .annotate-tool, .annotate-action {
    padding: 6px 12px;
    font-family: inherit;
    font-size: 13px;
    border: 1px solid #d4d4d4;
    background: #ffffff;
    color: #171717;
    border-radius: 6px;
    cursor: pointer;
  }
  .annotate-tool.annotate-active, .annotate-action.annotate-active {
    background: #171717;
    color: #ffffff;
    border-color: #171717;
  }
  .annotate-action:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .annotate-colors {
    display: flex;
    gap: 6px;
    padding: 0 8px;
    border-left: 1px solid #e5e5e5;
    border-right: 1px solid #e5e5e5;
  }
  .annotate-color {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 2px solid #ffffff;
    cursor: pointer;
    padding: 0;
    box-shadow: 0 0 0 1px #d4d4d4;
  }
  .annotate-color.annotate-active {
    box-shadow: 0 0 0 2px #171717;
  }
  .annotate-canvas-wrap {
    flex: 1;
    overflow: auto;
    background: #2a2a2a;
    border-radius: 8px;
    padding: 16px;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    width: 100%;
    box-sizing: border-box;
  }
  .annotate-canvas {
    max-width: 100%;
    height: auto;
    cursor: crosshair;
    background: #ffffff;
  }
  .annotate-text-input {
    position: absolute;
    font-family: inherit;
    font-size: ${TEXT_FONT_PX}px;
    font-weight: 600;
    padding: 2px 6px;
    border: 1px solid #c2410c;
    border-radius: 4px;
    background: #ffffff;
    outline: none;
    max-width: 240px;
  }
  .annotate-footer {
    display: flex;
    gap: 12px;
    margin-top: 12px;
  }
  .annotate-skip, .annotate-save {
    padding: 10px 24px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    border-radius: 6px;
    cursor: pointer;
    border: 0;
  }
  .annotate-skip {
    background: transparent;
    color: #fafafa;
    border: 1px solid #525252;
  }
  .annotate-save {
    background: #c2410c;
    color: #ffffff;
  }
  `;
}
